import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CiStatusBadge } from "@/components/ci/status-badge";
import { TriggerBadge } from "@/components/ci/trigger-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCiJobOverviews } from "@/hooks/use-ci";
import type { CiJobOverview } from "@/lib/types";
import { cn } from "@/lib/utils";

const KIND_FILTERS = [
  { label: "All", value: "all" },
  { label: "Nightly", value: "nightly" },
  { label: "Release", value: "tag" },
  { label: "Manual", value: "manual" },
  { label: "Mooncake test", value: "mctest" },
  { label: "Cryod test", value: "cryod-test" },
  { label: "Performance test", value: "perf" },
] as const;

const TIME_FILTERS = [
  { label: "All", value: "all" },
  { label: "Last 24h", value: "24h" },
  { label: "Last 7d", value: "7d" },
  { label: "Last 30d", value: "30d" },
  { label: "Custom range", value: "custom" },
] as const;

const TIME_HOURS: Record<string, number> = { "24h": 24, "7d": 168, "30d": 720 };

function jobDate(job: CiJobOverview): string {
  const iso = job.claimed_at ?? job.created_at;
  return iso
    ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "-";
}

function durationLabel(job: CiJobOverview): string {
  if (!job.claimed_at || !job.finished_at) return "-";
  const minutes = Math.round(
    (new Date(job.finished_at).getTime() - new Date(job.claimed_at).getTime()) / 60_000,
  );
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`;
}

function jobTimestamp(job: CiJobOverview): number {
  const iso = job.claimed_at ?? job.created_at;
  return iso ? new Date(iso).getTime() : 0;
}

export function CiJobsPage() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("all");
  // Kind is a server-side filter: the endpoint returns the newest rows of that
  // trigger, so a rare kind isn't crowded out by nightlies.
  const { data: jobs, isLoading } = useCiJobOverviews(
    kind === "all" ? undefined : { trigger: kind },
  );
  const [requestedBy, setRequestedBy] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // Everyone who launched a job — includes "ci" (the nightly launcher).
  const requesters = useMemo(() => {
    const names = new Set<string>();
    for (const job of jobs ?? []) if (job.requested_by) names.add(job.requested_by);
    return [...names].sort();
  }, [jobs]);

  const filtered = useMemo(() => {
    let rows = jobs ?? [];
    if (requestedBy !== "all") rows = rows.filter((job) => job.requested_by === requestedBy);
    const hours = TIME_HOURS[timeFilter];
    if (hours !== undefined) {
      const cutoff = Date.now() - hours * 3_600_000;
      rows = rows.filter((job) => jobTimestamp(job) >= cutoff);
    } else if (timeFilter === "custom") {
      const from = customFrom ? new Date(`${customFrom}T00:00:00Z`).getTime() : null;
      const to = customTo ? new Date(`${customTo}T23:59:59Z`).getTime() : null;
      rows = rows.filter((job) => {
        const ts = jobTimestamp(job);
        return (from === null || ts >= from) && (to === null || ts <= to);
      });
    }
    return rows;
  }, [jobs, requestedBy, timeFilter, customFrom, customTo]);

  return (
    <div className="mx-auto max-w-screen-xl space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold">CI Jobs</h1>
        <p className="text-sm text-muted-foreground">
          One row per orchestrated run — the badge says whether it was the nightly, a release
          qualification, or run by hand.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={kind}
          onValueChange={(value) => {
            if (!value) return;
            setKind(value);
            // The requester list comes from the new kind's rows; a stale pick
            // would show an empty table with no hint why.
            setRequestedBy("all");
          }}
        >
          <SelectTrigger className="w-44 gap-1">
            <span className="text-muted-foreground">Kind:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KIND_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={requestedBy} onValueChange={(value) => value && setRequestedBy(value)}>
          <SelectTrigger className="w-56 gap-1">
            <span className="text-muted-foreground">Requested by:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Anyone</SelectItem>
            {requesters.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={timeFilter} onValueChange={(value) => value && setTimeFilter(value)}>
          <SelectTrigger className="w-48 gap-1">
            <span className="text-muted-foreground">Time:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIME_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {timeFilter === "custom" && (
          <div className="flex items-center gap-1.5 text-sm">
            <input
              type="date"
              value={customFrom}
              onChange={(event) => setCustomFrom(event.target.value)}
              className="rounded-md border bg-transparent px-2 py-1 text-sm"
            />
            <span className="text-muted-foreground">–</span>
            <input
              type="date"
              value={customTo}
              onChange={(event) => setCustomTo(event.target.value)}
              className="rounded-md border bg-transparent px-2 py-1 text-sm"
            />
          </div>
        )}
        {filtered.length !== (jobs ?? []).length && (
          <span className="text-sm text-muted-foreground">
            {filtered.length} of {(jobs ?? []).length} jobs
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Cells</TableHead>
                <TableHead className="text-right">Regressions</TableHead>
                <TableHead className="text-right">Machines</TableHead>
                <TableHead className="text-right">Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((job) => (
                <TableRow
                  key={job.job_id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/ci/jobs/${job.job_id}`)}
                >
                  <TableCell className="font-medium">{jobDate(job)}</TableCell>
                  <TableCell className="font-mono text-xs">{job.job_id}</TableCell>
                  <TableCell>
                    <TriggerBadge trigger={job.trigger} releaseTag={job.release_tag} />
                  </TableCell>
                  <TableCell>
                    <CiStatusBadge status={job.state} />
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    <span className="text-emerald-500">{job.rollup.passed}✓</span>
                    {job.rollup.failed > 0 && (
                      <span className="ml-1.5 text-rose-500">{job.rollup.failed}✗</span>
                    )}
                    {job.rollup.xfailed > 0 && (
                      <span className="ml-1.5 text-yellow-600">{job.rollup.xfailed}xf</span>
                    )}
                    {job.rollup.flaky > 0 && (
                      <span className="ml-1.5 text-muted-foreground">{job.rollup.flaky}fl</span>
                    )}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right font-mono text-sm",
                      job.rollup.regressions > 0
                        ? "font-semibold text-rose-500"
                        : "text-muted-foreground",
                    )}
                  >
                    {job.rollup.regressions > 0 ? `${job.rollup.regressions} NEW` : "0"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {job.rollup.machines_used}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {durationLabel(job)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
